import { Tooltip } from '@mantine/core';
import { IconChartLine, IconGauge, IconTrendingUp } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDay } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import { SortableTh, type SortState, TablePager } from '../purchasing/table.js';
import classes from './Condition.module.css';
import type { Measurement, MeasurementPoint } from './condition-types.js';
import {
  limitChangeIds,
  limitLabel,
  measureNumber,
  resultKey,
  resultTone,
  sparkline,
} from './condition-view.js';
import { ResultBadge } from './MeasurementsStep.js';
import pms from './Pms.module.css';
import { paginate } from './plan-view.js';

const CHART = { width: 160, height: 40 };

/** Noktanın son durumu ve eğilimi: Component Details ▸ Maintenance Plan (K21). */
export function PointSummaryList({ points }: { points: readonly MeasurementPoint[] }) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.points.${key}`, options);
  const language = i18n.resolvedLanguage ?? 'en';
  if (points.length === 0) {
    return <EmptyState icon={<IconGauge size={22} stroke={1.8} />} title={k('none')} />;
  }
  return (
    <ul className={classes.pointList} aria-label={k('title')}>
      {points.map((point) => {
        const forecast = point.forecast;
        return (
          <li key={point.id}>
            <span className={classes.pointMain}>
              <strong>{point.name}</strong>
              <span className={classes.sub}>
                {`${k('limit')}: ${limitLabel(point, point.unit, language)}`}
              </span>
              <span className={classes.sub}>
                {point.last
                  ? k('last', {
                      value: measureNumber(language, point.last.value),
                      unit: point.unit,
                      date: formatDay(point.last.measuredAt.slice(0, 10)),
                    })
                  : k('noReading')}
              </span>
              {forecast && (forecast.alertOn || forecast.dangerOn) ? (
                <Tooltip label={k('estimatedHint', { count: forecast.basis })} multiline w={260}>
                  <span className={classes.forecast}>
                    <IconTrendingUp size={14} aria-hidden />
                    <span>{k('estimated')}</span>
                    {forecast.alertOn ? (
                      <span>{k('estimatedAlert', { date: formatDay(forecast.alertOn) })}</span>
                    ) : null}
                    {forecast.dangerOn ? (
                      <span>{k('estimatedDanger', { date: formatDay(forecast.dangerOn) })}</span>
                    ) : null}
                  </span>
                </Tooltip>
              ) : null}
            </span>
            <span className={classes.pointSide}>
              {point.last ? <ResultBadge result={String(point.last.result)} /> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Tek seri çizgi grafik: normal aralık bandı, noktalar sonuca göre renkli (dataviz; token). */
export function PointSparkline({
  point,
  measurements,
}: {
  point: MeasurementPoint;
  measurements: readonly Measurement[];
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const chart = sparkline(measurements, point, CHART);
  const live = measurements.filter((m) => !m.voided);
  const last = [...live].sort((a, b) => a.measuredAt.localeCompare(b.measuredAt)).at(-1);
  const value = last ? measureNumber(language, last.value) : '—';
  return (
    <figure className={classes.chart}>
      <figcaption className={classes.chartHead}>
        <strong title={point.name}>{point.name}</strong>
        <span className={classes.chartValue}>{last ? `${value} ${point.unit}` : '—'}</span>
      </figcaption>
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={t('pms.condition.history.chartLabel', {
          name: point.name,
          count: live.length,
          value,
          unit: point.unit,
        })}
      >
        {chart.band ? (
          <rect
            className={classes.band}
            x={0}
            width={CHART.width}
            y={chart.band.y1}
            height={Math.max(0, chart.band.y2 - chart.band.y1)}
          />
        ) : null}
        {chart.path ? <path className={classes.line} d={chart.path} /> : null}
        {chart.dots.map((dot) => (
          <circle
            key={dot.id}
            className={classes.dot}
            data-result={dot.result}
            cx={dot.x}
            cy={dot.y}
            r={2.2}
          />
        ))}
      </svg>
    </figure>
  );
}

type SortKey = 'date' | 'parameter' | 'value' | 'result';
const RESULT_RANK: Readonly<Record<string, number>> = { normal: 0, abnormal: 1, fail: 2 };

/**
 * Ölçüm geçmişi (Component Details ▸ History; plan M7 Task 21a): nokta başına küçük çizgi grafik ve
 * bütün ölçümler; sıralanır ve sayfalanır. İptal edilen ölçüm "VOID" ile kalır [NR496 4.3.2a]; limit
 * değişikliğinden sonraki ilk ölçüm işaretlidir [UR Z27 2.2.4].
 */
export function MeasurementHistory({
  points,
  measurements,
  pageSize = 8,
}: {
  points: readonly MeasurementPoint[];
  measurements: readonly Measurement[];
  pageSize?: number;
}) {
  const { t, i18n } = useTranslation();
  const tableBox = useFitColumns<HTMLElement>();
  const k = (key: string) => t(`pms.condition.history.${key}`);
  const language = i18n.resolvedLanguage ?? 'en';
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const names = useMemo(() => new Map(points.map((p) => [p.id, p.name])), [points]);
  const changed = useMemo(() => limitChangeIds(measurements), [measurements]);
  const rows = useMemo(() => {
    const value = (m: Measurement): string | number => {
      switch (sort.key) {
        case 'parameter':
          return names.get(m.pointId) ?? '';
        case 'value':
          return m.value;
        case 'result':
          return RESULT_RANK[String(m.result)] ?? 0;
        default:
          return m.measuredAt;
      }
    };
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...measurements].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      const order =
        typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sign * order || sign * a.measuredAt.localeCompare(b.measuredAt);
    });
  }, [measurements, sort, names]);
  if (measurements.length === 0) {
    return <EmptyState icon={<IconChartLine size={22} stroke={1.8} />} title={k('empty')} />;
  }
  const view = paginate(rows, page, pageSize);
  const columns: [SortKey, string][] = [
    ['date', k('date')],
    ['parameter', k('parameter')],
    ['value', k('value')],
    ['result', k('result')],
  ];
  return (
    <>
      <div className={classes.charts}>
        {points
          .filter((p) => measurements.some((m) => m.pointId === p.id))
          .map((p) => (
            <PointSparkline
              key={p.id}
              point={p}
              measurements={measurements.filter((m) => m.pointId === p.id)}
            />
          ))}
      </div>
      <section
        ref={tableBox}
        className={`${pms.tableBox} ${classes.historyBox}`}
        aria-label={t('pms.equipment.tableOf', { name: k('title') })}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={pms.table} data-compact data-cards>
          <thead>
            <tr>
              {columns.map(([key, label]) => (
                <SortableTh
                  key={key}
                  column={key}
                  label={label}
                  sort={sort}
                  numeric={key === 'value'}
                  drop={key === 'result' ? 1 : undefined}
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {view.rows.map((m) => (
              <tr key={m.id} className={m.voided ? classes.voided : undefined}>
                <td data-label={k('date')}>{formatDay(m.localDate)}</td>
                <td data-label={k('parameter')} className={classes.parameterCell}>
                  {names.get(m.pointId) ?? '—'}
                  {changed.has(m.id) ? (
                    <Tooltip label={k('limitChangedHint')} multiline w={240}>
                      <span className={classes.changed}>{k('limitChanged')}</span>
                    </Tooltip>
                  ) : null}
                </td>
                {/* Dar kartta sonuç sütunu gizlenir (`fit-columns.ts`); sonuç değerin renginde ve
                    adında kalır */}
                <td
                  data-label={k('value')}
                  data-numeric
                  data-tone={resultTone(String(m.result))}
                  title={t(`pms.condition.results.${resultKey(String(m.result))}`)}
                  className={classes.historyValue}
                >
                  {`${measureNumber(language, m.value)} ${m.unit}`}
                </td>
                <td data-label={k('result')} data-drop={1}>
                  <span className={classes.pointSide}>
                    <ResultBadge result={String(m.result)} />
                    {m.voided ? <StatusBadge tone="neutral">{k('voided')}</StatusBadge> : null}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {rows.length > pageSize ? (
        <TablePager
          page={view.page}
          pages={view.pages}
          summary={t('pms.work.showing', { from: view.from, to: view.to, total: view.total })}
          label={t('pms.work.pages', { name: k('title') })}
          onPage={setPage}
        />
      ) : null}
    </>
  );
}
